import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AuthShell, inputCls, labelCls } from "@/components/AuthShell";
import { RESET } from "@/constants/testIds/auth";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).join(" ") : "Something went wrong. Please try again.");

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(token ? "" : "This reset link is invalid. Please request a new one.");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(errText(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell testId={RESET.page} backTestId={RESET.backToLoginLink}
      title="Set a new password" subtitle="Choose a strong password you haven't used before.">
      {done ? (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-5 flex gap-3 items-start" data-testid={RESET.successMessage}>
          <CheckCircle2 className="w-5 h-5 terracotta shrink-0 mt-0.5" />
          <div>
            <p className="text-charcoal font-semibold">Your password has been reset.</p>
            <p className="text-sm text-charcoal/60 mt-1">Redirecting you to sign in...</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>New password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className={inputCls} autoComplete="new-password" data-testid={RESET.passwordInput} />
          </div>
          <div>
            <label className={labelCls}>Confirm password</label>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className={inputCls} autoComplete="new-password" data-testid={RESET.confirmInput} />
          </div>
          {error && <p className="text-sm terracotta font-medium" data-testid={RESET.errorMessage}>{error}</p>}
          <button type="submit" disabled={busy || !token} className="w-full pill-btn pill-btn-primary mt-2" data-testid={RESET.submitButton}>
            {busy ? "Updating..." : "Reset password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
